<template>
  <v-card flat class="settings-card">
    <!-- <v-card-title class="settings-header-text">Call Settings</v-card-title> -->
    <v-container>
      <v-form ref="form">
        <v-card-title class="settings-header-text">
          Review Sources
        </v-card-title>
        <v-card-subtitle class="px-mdAndUp-0">
          Select one or more review sources for us to track the history for your
          business
        </v-card-subtitle>
        <div class="settings-section-main-content">
          <v-select
            :value="reviewSettings['activeReviewTypes']"
            @change="updateConfig('activeReviewTypes', $event)"
            :items="bizReviewTypes"
            hint="Select the services to configure them"
            persistent-hint
            item-text="name"
            item-value="code"
            label="Review Types"
            placeholder="None selected"
            multiple
            chips
            outlined
          >
            <template v-slot:selection="{ attrs, item, select, selected }">
              <v-chip
                v-bind="attrs"
                :input-value="selected"
                close
                @click:close="removeButton(item)"
                small
              >
                <strong>{{ item.name }}</strong>
              </v-chip>
            </template>
          </v-select>
        </div>
        <div class="review-type-configs mt-8 settings-section-main-content">
          <v-card-subtitle class="px-0 text-h6">
            Configure each review source below
          </v-card-subtitle>
          <v-row>
            <v-col cols="12" v-if="hasService('google')">
              <v-text-field
                ref="googlePlaceId"
                label="Google Place ID"
                placeholder="MyBusinessPage"
                hint="Use the Google Place ID lookup tool by clicking the button on the right"
                persistent-hint
                :value="googlePlaceId"
                @input="updateConfig('googlePlaceId', $event)"
                class="py-1 open-button-col"
                required
                :rules="[rules.required]"
                dense
                outlined
              >
                <template v-slot:append-outer>
                  <v-btn outlined @click="openGoogleLookupDialog">
                    <v-icon left>mdi-map-marker</v-icon>
                    Lookup
                  </v-btn>
                  <v-btn
                    outlined
                    @click="openGoogleUrl"
                    class="try-review-source-btn"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" v-if="hasService('facebook')">
              <v-text-field
                ref="facebookPageName"
                label="Facebook Page Name"
                placeholder="MyBusinessPageName"
                hint="Look at the reviews URL of your Facebook page, and fill in the blank"
                persistent-hint
                :value="facebookPageName"
                @input="updateConfig('facebookPageId', $event)"
                prefix="https://www.facebook.com/"
                suffix="/reviews/"
                class="py-1 open-button-col"
                required
                :rules="[rules.required]"
                dense
                outlined
              >
                <template v-slot:append-outer>
                  <v-btn
                    outlined
                    @click="openFacebookUrl"
                    class="try-review-source-btn"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" v-if="hasService('yelp')">
              <v-text-field
                ref="yelpPageName"
                label="Yelp Page Name"
                placeholder="my-business-page-name"
                hint="Look at the URL of your Yelp page, and fill in the blank"
                persistent-hint
                :value="yelpPageName"
                @input="updateConfig('yelpPageId', $event)"
                prefix="https://www.yelp.com/biz/"
                class="py-1 open-button-col"
                required
                :rules="[rules.required]"
                dense
                outlined
              >
                <template v-slot:append-outer>
                  <v-btn
                    outlined
                    @click="openYelpUrl"
                    class="try-review-source-btn"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-6 mb-10 mr-md-0 mr-1 float-right">
            <v-btn
              color="primary"
              @click="onSave"
              class="mb-8 px-8"
              :disabled="!(reviewSettings ? reviewSettings.isDirty : false)"
              >Save</v-btn
            >
          </v-row>
        </div>
      </v-form>
      <v-dialog
        v-model="googleLookupDialog"
        max-width="600"
        style="background:white"
      >
        <google-place-id-lookup-modal></google-place-id-lookup-modal>
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import { mask } from "vue-the-mask";
const GooglePlaceIdLookupModal = () =>
  import(
    /* webpackChunkName: "components-settings-modals-GooglePlaceIDLookupModal" */ `../../settings/modals/GooglePlaceIDLookupModal.vue`
  );

export default {
  name: "ReviewSources",
  components: {
    GooglePlaceIdLookupModal
  },
  directives: {
    mask
  },
  data: function() {
    return {
      isFalse: false,
      isTrue: true,
      googleLookupDialog: false,
      showDialogContainer: false,
      rules: {
        required: value => !!value || "Required"
      }
    };
  },
  computed: {
    settingsNavSelected() {
      return this.$parent.$parent.$parent.$parent.settingsNavSelected;
    },
    account() {
      return this.$store.getters["entities/accounts/query"]()
        .withAllRecursive(1)
        .first();
    },
    settings() {
      return this.$store.getters["entities/settings/query"]().first();
    },
    reviewSettings() {
      const tempSettings = this.$store.getters[
        "entities/bizReviewSettings"
      ]().first();
      return tempSettings ? tempSettings : {};
    },
    reviewSettingsConfig() {
      const tempSettings = this.$store.getters[
        "entities/bizReviewSettings"
      ]().first();
      return tempSettings ? tempSettings.config : {};
    },
    bizReviewTypes() {
      let bizReviewTypes = this.$store.getters[
        "entities/bizReviewTypes/query"
      ]().get();
      return bizReviewTypes;
    },
    googlePlaceId() {
      return this.reviewSettingsConfig["googlePlaceId"];
    },
    facebookPageName() {
      return this.reviewSettingsConfig["facebookPageId"];
    },
    yelpPageName() {
      return this.reviewSettingsConfig["yelpPageId"];
    }
  },
  async created() {
    this.$store.dispatch("entities/bizReviewTypes/hydrate");
    this.$store.dispatch("entities/bizReviewSettings/hydrate", 0);
  },
  methods: {
    removeButton(item) {
      this.updateConfig(
        "activeReviewTypes",
        this.reviewSettingsConfig["activeReviewTypes"].filter(val => {
          return val != item;
        })
      );
    },
    updateConfig(name, value) {
      let vueThis = this;
      let newConfig = JSON.parse(JSON.stringify(this.reviewSettingsConfig));
      let data = {
        isDirty: true
      };
      const dataFields = ["activeReviewTypes"];
      if (dataFields.includes(name)) {
        data[name] = value;
      } else {
        // let configFieldNames = this.bizReviewTypes.map(val => {
        //   return val.configField;
        // });
        const tempValue = value.split(/[/?]+/);
        console.log(tempValue);
        if (name == "facebookPageId") {
          if (tempValue[1] == "www.facebook.com" && tempValue[2] == "pg") {
            value = tempValue[3];
          } else if (tempValue[1] == "www.facebook.com") {
            value = tempValue[2];
          }
        } else if (
          name == "yelpPageId" &&
          tempValue[1] == "www.yelp.com" &&
          tempValue[2] == "biz"
        ) {
          value = tempValue[3];
        }
        newConfig[name] = value;
        data.config = newConfig;
      }
      this.$store.dispatch("entities/bizReviewSettings/update", {
        where: vueThis.reviewSettings.id,
        data: data
      });
    },
    onSave(evt) {
      evt.preventDefault();
      let vueThis = this;
      this.$store.dispatch(
        "entities/bizReviewSettings/updateBizReviewsSettings",
        vueThis.reviewSettings
      );
    },
    hasService(name) {
      if (
        this.reviewSettings &&
        this.reviewSettings["activeReviewTypes"] &&
        this.reviewSettings["activeReviewTypes"].includes(name)
      ) {
        return true;
      }
      return false;
    },
    openGoogleLookupDialog() {
      this.googleLookupDialog = true;
    },
    openGoogleUrl() {
      const url =
        "https://search.google.com/local/writereview?placeid=" +
        this.googlePlaceId;
      window.open(url, "_blank");
    },
    openFacebookUrl() {
      // const url =
      //   "https://www.facebook.com/login/?next=https%3A%2F%2Fwww.facebook.com%2F" +
      //   this.facebookPageName +
      //   "%2Freviews%2F";
      const url = `https://www.facebook.com/${this.facebookPageName}/reviews/`
      window.open(url, "_blank");
    },
    openYelpUrl() {
      const url = "https://www.yelp.com/biz/" + this.yelpPageName;
      window.open(url, "_blank");
    }
  },
  watch: {}
};
</script>

<style lang="stylus">
div.spacer {
  width: 3rem;
}
div.open-button-col.v-input.v-text-field div.v-input__append-outer {
  margin: .1rem 0 .1rem .25rem !important;
}
div.open-button-col.v-input.v-text-field button.try-review-source-btn {
  margin-left: .25rem;
}
</style>
